import React from "react";
import {Box, Chip, Stack, Tooltip, useTheme} from "@mui/material";
import {DailyReportAlarmSummary} from "../types/DailyReportAlarmSummary.types";
import {
    buildDailyReportAlarmCellModel,
    DailyReportAlarmCategory,
    DailyReportAlarmCategoryItem,
} from "../utils/DailyReportAlarmSummary.utils";

// Mesmos valores dos chips da coluna Alarmes da Visão Geral (PlantAlarmSummaryCell, SGD-3764).
const BASE_CHIP_SX = {
    height: 24,
    borderRadius: "100px",
    paddingLeft: "4px",
    paddingRight: "4px",
    fontFamily: '"Work Sans", sans-serif',
    fontWeight: 600,
    fontSize: 13,
    lineHeight: "18px",
    letterSpacing: "0.16px",
    "& .MuiChip-label.MuiChip-label": {paddingLeft: "6px", paddingRight: "6px"},
    "&:focus-visible": {outline: "2px solid #2D5DE7", outlineOffset: 2},
};

type ChipColors = {color: string; backgroundColor: string};

// Comunicação usina usa as cores do chip "Usina sem comunicação" da Visão Geral.
const CATEGORY_COLORS: Record<DailyReportAlarmCategory, ChipColors> = {
    POWER_STATION_COMMUNICATION: {color: "#FFFFFF", backgroundColor: "rgba(23, 23, 23, 0.8)"},
    CRITICAL: {color: "#FC531F", backgroundColor: "rgba(252, 83, 31, 0.1)"},
    EQUIPMENT_COMMUNICATION: {color: "#FFFFFF", backgroundColor: "rgba(255, 120, 0, 0.8)"},
    NON_CRITICAL: {color: "#F57F17", backgroundColor: "rgba(249, 168, 37, 0.1)"},
};

// Como na Visão Geral, o tema escuro só reforça o fundo de Comunicação usina; as demais cores se mantêm.
const DARK_CATEGORY_COLORS: Partial<Record<DailyReportAlarmCategory, Partial<ChipColors>>> = {
    POWER_STATION_COMMUNICATION: {backgroundColor: "rgba(23, 23, 23, 0.95)"},
};

const GROUPED_COLORS: ChipColors = {color: "#FFFFFF", backgroundColor: "rgba(249, 168, 37, 0.9)"};

const getCategoryColors = (category: DailyReportAlarmCategory, isDark: boolean): ChipColors => ({
    ...CATEGORY_COLORS[category],
    ...(isDark ? DARK_CATEGORY_COLORS[category] : {}),
});

const formatItemLabel = (item: DailyReportAlarmCategoryItem) => `${item.displayCount} ${item.label}`;

export interface DailyAlarmSummaryCellProps {
    powerStationName: string;
    alarmSummary: DailyReportAlarmSummary | null | undefined;
}

export default function DailyAlarmSummaryCell({powerStationName, alarmSummary}: DailyAlarmSummaryCellProps) {
    const isDark = useTheme().palette.mode === "dark";
    const {visible, grouped} = buildDailyReportAlarmCellModel(alarmSummary);

    if (visible.length === 0 && grouped.length === 0) {
        return null;
    }

    const groupedLabel = `+${grouped.length} outros`;

    return (
        <Stack direction="column" alignItems="flex-start" sx={{gap: "4px", py: 1.5}}>
            {visible.map((item) => (
                <Chip
                    key={item.category}
                    label={formatItemLabel(item)}
                    aria-label={`${item.displayCount} alarmes ${item.label} da usina ${powerStationName}`}
                    sx={{...BASE_CHIP_SX, ...getCategoryColors(item.category, isDark)}}
                />
            ))}
            {grouped.length > 0 && (
                <Tooltip
                    arrow
                    title={
                        <Box component="span" sx={{display: "flex", flexDirection: "column"}}>
                            {grouped.map((item) => (
                                <span key={item.category}>{formatItemLabel(item)}</span>
                            ))}
                        </Box>
                    }
                >
                    <Chip
                        tabIndex={0}
                        label={groupedLabel}
                        aria-label={`${groupedLabel} da usina ${powerStationName}: ${grouped.map(formatItemLabel).join(", ")}`}
                        sx={{...BASE_CHIP_SX, ...GROUPED_COLORS}}
                    />
                </Tooltip>
            )}
        </Stack>
    );
}
